import type { ProductCatalogEntry } from '@/types/products'

/**
 * ISBT 128 catalog column metadata — labels, the summary-table column set, and
 * the export shape.
 *
 * Pure and dependency-free so it can be unit-tested: `vitest.config.ts` is
 * `environment: 'node'` with `include: ['src/**\/*.test.ts']`, so a `.tsx` test
 * would silently never run. The tables and the detail dialog all read their
 * columns from here, which is what stops the screen, the popup and the export
 * from disagreeing about what a column is called.
 */

export type IsbtFieldKey = keyof Omit<ProductCatalogEntry, 'id' | 'createdAt' | 'updatedAt'>

export type IsbtField = {
  key: IsbtFieldKey
  /** Header text. Matches the source workbook's column name, de-underscored. */
  label: string
  /** Which part of the HL7 description this column was parsed out of. */
  group: 'identity' | 'product' | 'triplet' | 'attribute' | 'source'
}

/**
 * All 44 columns, in the source file's order.
 *
 * The order is the file's, not a designer's, so a reader comparing the detail
 * popup against the spreadsheet goes straight down both.
 */
export const ISBT_FIELDS: IsbtField[] = [
  { key: 'productDescriptionCode', label: 'Product Code', group: 'identity' },
  { key: 'inUse', label: 'In Use', group: 'identity' },
  { key: 'expired', label: 'Expired', group: 'identity' },

  { key: 'productClass', label: 'Product Class', group: 'product' },
  { key: 'sourceTissue', label: 'Source Tissue', group: 'product' },
  { key: 'collectionModifier', label: 'Collection Modifier', group: 'product' },
  { key: 'stateModifier', label: 'State Modifier', group: 'product' },

  { key: 'anticoagulant', label: 'Anticoagulant', group: 'triplet' },
  { key: 'collectionVolume', label: 'Collection Volume', group: 'triplet' },
  { key: 'storageTemperature', label: 'Storage Temperature', group: 'triplet' },

  { key: 'containers', label: 'Containers', group: 'attribute' },
  { key: 'containerAutomation', label: 'Container Automation', group: 'attribute' },
  { key: 'unitCount', label: 'Unit Count', group: 'attribute' },
  { key: 'donorCount', label: 'Donor Count', group: 'attribute' },
  { key: 'volume', label: 'Volume', group: 'attribute' },
  { key: 'plateletContent', label: 'Platelet Content', group: 'attribute' },
  { key: 'hematocrit', label: 'Hematocrit', group: 'attribute' },
  { key: 'cryoprotectant', label: 'Cryoprotectant', group: 'attribute' },
  { key: 'additiveAdded', label: 'Additive Added', group: 'attribute' },
  { key: 'componentReduction', label: 'Component Reduction', group: 'attribute' },
  { key: 'cellSelection', label: 'Cell Selection', group: 'attribute' },
  { key: 'pathogenReduction', label: 'Pathogen Reduction', group: 'attribute' },
  { key: 'irradiation', label: 'Irradiation', group: 'attribute' },
  { key: 'preservationState', label: 'Preservation State', group: 'attribute' },
  { key: 'processing', label: 'Processing', group: 'attribute' },
  { key: 'manipulationExtent', label: 'Manipulation Extent', group: 'attribute' },
  { key: 'collectionDetail', label: 'Collection Detail', group: 'attribute' },
  { key: 'microbialTesting', label: 'Microbial Testing', group: 'attribute' },
  { key: 'intendedUse', label: 'Intended Use', group: 'attribute' },
  { key: 'antigenSpecificity', label: 'Antigen Specificity', group: 'attribute' },
  { key: 'residualLeukocytes', label: 'Residual Leukocytes', group: 'attribute' },
  { key: 'otherAdditives', label: 'Other Additives', group: 'attribute' },
  { key: 'thirdPartyComponents', label: 'Third Party Components', group: 'attribute' },
  { key: 'forManufacturing', label: 'For Manufacturing', group: 'attribute' },
  { key: 'quarantine', label: 'Quarantine', group: 'attribute' },
  { key: 'cultured', label: 'Cultured', group: 'attribute' },
  { key: 'finalContainer', label: 'Final Container', group: 'attribute' },
  { key: 'geneticallyModified', label: 'Genetically Modified', group: 'attribute' },
  { key: 'pooledSingleDonor', label: 'Pooled Single Donor', group: 'attribute' },
  { key: 'lowVolume', label: 'Low Volume', group: 'attribute' },
  { key: 'granulocytesPrep', label: 'Granulocytes Prep', group: 'attribute' },
  { key: 'attributeCount', label: 'Attribute Count', group: 'attribute' },
  { key: 'unparsedAttributes', label: 'Unparsed Attributes', group: 'attribute' },

  { key: 'description', label: 'Description', group: 'source' },
]

/** Label lookup, so a caller never hand-writes a header string. */
export const ISBT_LABELS: Record<IsbtFieldKey, string> = Object.fromEntries(
  ISBT_FIELDS.map(f => [f.key, f.label]),
) as Record<IsbtFieldKey, string>

/**
 * The columns the table shows, in the order the spec gave them.
 *
 * Eleven of the 44. Everything else is one click away in the detail dialog and
 * present in the export — the table is a summary, not the record.
 */
export const SUMMARY_COLUMN_KEYS: IsbtFieldKey[] = [
  'productDescriptionCode',
  'productClass',
  'sourceTissue',
  'anticoagulant',
  'storageTemperature',
  'cryoprotectant',
  'preservationState',
  'thirdPartyComponents',
  'geneticallyModified',
  'description',
  'processing',
]

/** Render one cell. Booleans read as Yes/No; '' is "not stated", not missing. */
export function formatIsbtValue(
  entry: ProductCatalogEntry,
  key: IsbtFieldKey,
  blank = '—',
): string {
  const value = entry[key]
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') return String(value)
  return value?.trim() ? value : blank
}

/**
 * One export row: ALL 44 fields, not the 11 on screen.
 *
 * Keyed by the display label so the exported file is readable next to the
 * source workbook. Blanks stay blank rather than becoming the table's em-dash —
 * an export is data, and '—' in a spreadsheet cell is not.
 */
export function toExportRow(entry: ProductCatalogEntry): Record<string, string> {
  return Object.fromEntries(
    ISBT_FIELDS.map(field => [field.label, formatIsbtValue(entry, field.key, '')]),
  )
}

/** Header row for the export, in the same order as `toExportRow`'s keys. */
export const EXPORT_HEADERS: string[] = ISBT_FIELDS.map(f => f.label)

/**
 * One filled-in row for the downloadable template, in `ISBT_FIELDS` order.
 *
 * The spec's worked example (S2487), so whoever opens the template sees how a
 * real description maps onto the 44 columns — in particular that `Description`
 * holds the whole original string and the other columns are its parts, not a
 * replacement for it.
 */
export const TEMPLATE_EXAMPLE_ROW: (string | number)[] = ISBT_FIELDS.map(field => {
  const example: Partial<Record<IsbtFieldKey, string | number>> = {
    productDescriptionCode: 'S2487',
    inUse: 1,
    expired: 0,
    productClass: 'NK CELLS',
    sourceTissue: 'APHERESIS',
    anticoagulant: 'Citrate',
    storageTemperature: '<=-150C',
    cryoprotectant: '10% DMSO',
    cellSelection: 'Alpha Beta T cell reduced',
    preservationState: 'Cryopreserved',
    processing: 'Electroporated',
    otherAdditives: 'Yes incl animal src',
    thirdPartyComponents: 'Yes',
    cultured: 'Yes',
    geneticallyModified: 'Yes',
    attributeCount: 8,
    description:
      'NK CELLS, APHERESIS|Citrate/XX/<=-150C|Electroporated|10% DMSO|3rd Party Comp:Yes|Other Additives:Yes incl animal src|Genetically Modified:Yes|Cryopreserved|Cultured:Yes|Alpha Beta T cell reduced',
  }
  return example[field.key] ?? ''
})
